import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Truck, Search, ArrowRight, MapPin } from "lucide-react";
import { STATES } from "@/data/states";
import QuoteWizard from "@/components/QuoteWizard";

export default function MoversIndex() {
  const [q, setQ] = useState("");
  const [wizardOpen, setWizardOpen] = useState(false);
  const filtered = STATES.filter((s) =>
    s.name.toLowerCase().includes(q.toLowerCase()) || s.code.toLowerCase().includes(q.toLowerCase())
  );

  useEffect(() => {
    document.title = "Movers Directory — All 50 States | VanLines Scout";
    let desc = document.head.querySelector('meta[name="description"]');
    if (!desc) {
      desc = document.createElement("meta");
      desc.setAttribute("name", "description");
      document.head.appendChild(desc);
    }
    desc.setAttribute("content", "Find vetted, licensed movers in every U.S. state. Compare free quotes from up to 4 moving companies near you.");
  }, []);

  return (
    <div className="min-h-screen bg-movewave-paper text-movewave-ink">
      {/* Header */}
      <header className="sticky top-0 z-40 bg-white/85 backdrop-blur-xl border-b-2 border-movewave-ink">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2" data-testid="brand-logo">
            <div className="w-8 h-8 bg-movewave-blue flex items-center justify-center">
              <Truck className="w-4 h-4 text-white" strokeWidth={2.5} />
            </div>
            <span className="font-display font-black text-xl tracking-tight">
              VanLines <span className="text-movewave-blue">Scout</span>
            </span>
          </Link>
          <Button
            onClick={() => setWizardOpen(true)}
            className="bg-movewave-blue hover:bg-movewave-blue-hover text-white font-bold uppercase tracking-wider rounded-none h-10 px-5 text-xs"
            data-testid="header-cta-btn"
          >
            Get free quote
          </Button>
        </div>
      </header>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-12 sm:pt-20 pb-14">
        <div className="text-xs uppercase tracking-[0.2em] font-bold text-movewave-blue mb-3">
          Movers directory
        </div>
        <h1 className="font-display font-black text-5xl sm:text-6xl lg:text-7xl leading-[0.95] tracking-tighter mb-6">
          Movers in every<br /><span className="text-movewave-blue">U.S. state.</span>
        </h1>
        <p className="text-lg text-gray-700 max-w-2xl leading-relaxed mb-8">
          Pick your state to compare vetted movers in your area. Free quotes, no spam, no obligation.
        </p>

        <div className="relative max-w-md">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <Input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search states... (e.g. California, TX, FL)"
            className="pl-9 h-12 rounded-none border-2 border-movewave-ink focus:border-movewave-blue focus-visible:ring-0 bg-white"
            data-testid="states-search-input"
          />
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-24">
        {filtered.length === 0 ? (
          <div className="border-2 border-dashed border-gray-300 p-12 text-center text-gray-500 bg-white">
            No states match "{q}".
          </div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
            {filtered.map((s) => (
              <Link
                key={s.slug}
                to={`/movers/${s.slug}`}
                className="group border-2 border-movewave-ink p-5 bg-white hover:bg-movewave-blue hover:text-white transition-colors flex flex-col justify-between"
                data-testid={`state-card-${s.code}`}
              >
                <div>
                  <div className="text-xs uppercase tracking-wider font-bold opacity-50 group-hover:opacity-80 mb-2">
                    {s.code}
                  </div>
                  <div className="font-display font-bold text-lg tracking-tight">
                    {s.name}
                  </div>
                  <div className="text-xs mt-1 opacity-70">{s.topCities.length} cities covered</div>
                </div>
                <div className="flex items-center gap-1 mt-4 text-xs font-bold uppercase tracking-wider">
                  <MapPin className="w-3 h-3" /> Movers <ArrowRight className="w-3 h-3 ml-1 group-hover:translate-x-1 transition-transform" />
                </div>
              </Link>
            ))}
          </div>
        )}
      </section>

      <footer className="bg-movewave-ink text-white py-14 border-t-2 border-movewave-ink">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center gap-2 mb-4">
            <div className="w-8 h-8 bg-movewave-orange flex items-center justify-center">
              <Truck className="w-4 h-4 text-white" strokeWidth={2.5} />
            </div>
            <span className="font-display font-black text-2xl tracking-tight">
              VanLines <span className="text-movewave-orange">Scout</span>
            </span>
          </div>
          <div className="text-xs text-white/50 mt-6">© {new Date().getFullYear()} VanLines Scout. All rights reserved.</div>
        </div>
      </footer>

      <QuoteWizard open={wizardOpen} onOpenChange={setWizardOpen} />
    </div>
  );
}
